import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { fmtFull } from "../../shared/format.js";
import { rootRoute } from "../../app/root-route.js";
import { useWall } from "./wall.api.js";
import { Slideshow } from "./Slideshow.js";
import { FaeriBackdrop } from "./FaeriBackdrop.js";

export const showRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/show/$slug",
  validateSearch: (s: Record<string, unknown>): { t?: string } => ({
    t: typeof s.t === "string" ? s.t : undefined,
  }),
  component: WallScreen,
});

function WallScreen() {
  const { slug } = showRoute.useParams();
  const { t } = showRoute.useSearch();
  const { data, isLoading, error } = useWall(slug, t);

  // masque le curseur après inactivité (projection)
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let to: number;
    const bump = () => {
      setIdle(false);
      window.clearTimeout(to);
      to = window.setTimeout(() => setIdle(true), 3000);
    };
    bump();
    window.addEventListener("mousemove", bump);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.clearTimeout(to);
    };
  }, []);

  if (isLoading) {
    return <Fullscreen>Préparation de la projection…</Fullscreen>;
  }
  if (error || !data) {
    return <Fullscreen>Lien de projection invalide.</Fullscreen>;
  }

  const couple = `${data.wedding.partner1Name} & ${data.wedding.partner2Name}`;

  return (
    <div
      data-theme={data.event.theme}
      className={`fixed inset-0 overflow-hidden bg-black ${idle ? "cursor-none" : ""}`}
    >
      <Slideshow items={data.items} fallback={data.couplePhotoUrls} coupleName={couple} />
      <FaeriBackdrop />

      {/* carte d'infos organisateur */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 1 }}
        className="absolute left-[3vw] top-[3vw] max-w-[34vw] rounded-3xl bg-black/35 p-[1.6vw] backdrop-blur-md"
      >
        <p className="text-[1vw] uppercase tracking-[0.35em] text-white/70">
          {data.event.name}
        </p>
        <h1 className="mt-1 font-serif text-[3.4vw] leading-none text-white">{couple}</h1>
        <p className="mt-2 text-[1.1vw] text-white/80">
          {fmtFull(data.event.date)}
          {data.event.location ? ` · ${data.event.location}` : ""}
        </p>
        {(data.wallMessage || data.wedding.story) && (
          <p className="mt-3 font-serif text-[1.4vw] italic text-white/90">
            « {data.wallMessage || data.wedding.story} »
          </p>
        )}
      </motion.div>

      {/* QR "ajoutez vos photos" */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.7, duration: 0.8 }}
        className="absolute bottom-[3vw] right-[3vw] flex items-center gap-[1vw] rounded-2xl bg-white/90 p-[1vw]"
      >
        <img src={data.qrDataUrl} alt="QR" className="h-[7vw] w-[7vw]" />
        <div className="pr-[0.5vw] text-stone-800">
          <p className="font-serif text-[1.3vw] leading-tight">Ajoutez vos photos</p>
          <p className="text-[0.9vw] text-stone-500">Scannez pour contribuer</p>
        </div>
      </motion.div>

      {data.items.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="rounded-2xl bg-black/40 px-[2vw] py-[1vw] font-serif text-[1.6vw] text-white backdrop-blur">
            En attente des premières photos des invités…
          </p>
        </div>
      )}
    </div>
  );
}

function Fullscreen({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black text-lg text-white/80">
      {children}
    </div>
  );
}
